// @ts-nocheck
import React, {useState, useEffect, useMemo, useCallback} from 'react';
import ReactDOM from "react-dom";
import {AddToQuote} from "../quote/components/addToQuote";

import { calcTypeOptions, depthOptions, mulchConfig, PI } from '../quote/constants';

let cachedMulchDomContext = null;

function getMulchDomContext() {
  if (cachedMulchDomContext) {
    return cachedMulchDomContext;
  }
  const productForm = document.querySelector('form[action*="/cart/add"]');
  cachedMulchDomContext = {
    productForm,
    quantityElement: productForm?.querySelector('.quantity-selector__value'),
    selector: document.querySelector('[id^="product-select-"]'),
  };
  return cachedMulchDomContext;
}

function getVariantMulchWeight(selector, variantId) {
  const selectedVariant = selector?.querySelector('[value="' + variantId + '"]');
  return Number(selectedVariant?.getAttribute('data-weight')?.replace(' lb', '').trim()) || 50;
}

function computeMulchAmount({ calcType, depth, dimensions, mulchWeight, mulchVolume, isBlogMode }) {
  let amount = 0;
  const { area, length, width, cubic_feet, cubic_yards, diameter } = dimensions;

  switch (calcType) {
    case 'area':
      amount = isBlogMode
        ? Number(area) * (depth / 12) * mulchVolume
        : Number(area) * (depth / 12) * (mulchWeight / mulchVolume);
      break;
    case 'length_width':
      amount = (Number(length) * Number(width)) * (depth / 12) * (mulchWeight / mulchVolume);
      break;
    case 'cubic_feet':
      amount = Number(cubic_feet) * mulchVolume;
      break;
    case 'cubic_yards':
      amount = Number(cubic_yards) * mulchVolume * 27;
      break;
    case 'diameter': {
      const diameterNumber = Number(diameter);
      amount = (0.25 * diameterNumber * diameterNumber * PI) * (depth / 12) * mulchVolume;
      break;
    }
  }

  return amount;
}

export const initializeMulchCalculator = (mode = 'product') => {
  const elementId = mode === 'blog' ? 'mulch-calculator-blog' : 'mulch-calculator';
  if (document.getElementById(elementId)) {
    ReactDOM.render(
      React.createElement(mulchCalculator, { mode }),
      document.getElementById(elementId)
    );
  }
}

export const initializeMulchCalculatorBlog = () => {
  initializeMulchCalculator('blog');
}

export const mulchCalculator = ({ mode = 'product' }) => {
  const isBlogMode = mode === 'blog';
  const domContext = useMemo(() => (isBlogMode ? null : getMulchDomContext()), [isBlogMode]);

  const initialMulchWeight = isBlogMode
    ? mulchConfig.ground_smart_weight
    : getVariantMulchWeight(domContext?.selector, initialVariant.id);

  const [variant, setVariant] = useState(isBlogMode ? null : initialVariant);
  const [calcType, setCalcType] = useState('area');
  const [depth, setDepth] = useState(Number(depthOptions[mulchConfig.default_depth_index].value));
  const [dimensions, setDimensions] = useState({ area: '', length: '', width: '', cubic_feet: '', cubic_yards: '', diameter: '' });
  const [mulchWeight, setMulchWeight] = useState(initialMulchWeight);
  const [mulchVolume, setMulchVolume] = useState(
    isBlogMode ? mulchConfig.mulch_volume : initialMulchWeight / mulchConfig.mulch_volume
  );

  const rawAmount = useMemo(
    () => computeMulchAmount({ calcType, depth, dimensions, mulchWeight, mulchVolume, isBlogMode }),
    [calcType, depth, dimensions, mulchWeight, mulchVolume, isBlogMode]
  );

  const mulchAmount = useMemo(() => Math.ceil(rawAmount), [rawAmount]);
  const numMulchBags = useMemo(
    () => (mulchWeight > 0 ? Math.ceil(rawAmount / mulchWeight) : 0),
    [rawAmount, mulchWeight]
  );

  const updateVariant = useCallback((e) => {
    if (isBlogMode || !domContext?.selector) {
      return;
    }
    const nextVariant = e.detail.variant;
    setVariant(nextVariant);
    const newWeight = getVariantMulchWeight(domContext.selector, nextVariant.id);
    setMulchWeight(newWeight);
    setMulchVolume(newWeight / mulchConfig.mulch_volume_per_pound);
  }, [isBlogMode, domContext]);

  useEffect(() => {
    if (isBlogMode) {
      return;
    }
    window.addEventListener('variant:changed', updateVariant);
    return () => window.removeEventListener('variant:changed', updateVariant);
  }, [isBlogMode, updateVariant]);

  useEffect(() => {
    if (!(rawAmount > 0)) {
      return;
    }
    const timeout = setTimeout(() => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'mulch_calculator',
        value: rawAmount,
        mulch_amount: rawAmount,
      });
    }, 300);
    return () => clearTimeout(timeout);
  }, [rawAmount, calcType]);

  const toggleCalcType = (e) => {
    setCalcType(e.target.value);
  };

  const toggleDepth = (e) => {
    setDepth(Number(e.target.value));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDimensions(prev => ({ ...prev, [name]: value }));
  };

  const addToCart = (e) => {
    e.preventDefault();

    let selectedItem = {
      id: isBlogMode ? mulchConfig.mulch_variant_id : variant.id,
      quantity: numMulchBags
    }

    fetch("".concat(window.routes.cartAddUrl, ".js"), {
      body: JSON.stringify(selectedItem),
      credentials: 'same-origin',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    }).then(function (response) {
      if (response.ok) {
        document.documentElement.dispatchEvent(new CustomEvent('product:added', {
          bubbles: true,
          detail: {
            variant: selectedItem,
            quantity: parseInt(numMulchBags)
          }
        }));
      } else {
        response.json().then((content) => {
          console.log('content', content, 'response', response, 'selectedItem', selectedItem, 'variant', variant, 'numMulchBags', numMulchBags);
        });
      }
    });
  }

  const renderInputFields = () => {
    if (calcType === 'length_width') {
      return (
        <div className="form__input-row length-width-group">
          {['length', 'width'].map(field => (
            <div key={field} className="form-group">
              <label className="control-label" htmlFor={field}>{field.charAt(0).toUpperCase() + field.slice(1) + ' (Ft.)'}</label>
              <input
                type="number" name={field} id={field} value={dimensions[field]}
                onChange={handleInputChange} placeholder={`Enter the ${field}`}
                className="form-control input-md focus:ring-2"
              />
            </div>
          ))}
        </div>
      );
    }

    const calcTypeOption = calcTypeOptions.find(opt => opt.value === calcType);
    return (
      <div className="form-group">
        <label className="control-label" htmlFor={calcType}>{calcTypeOption.name}</label>
        <input
          type="number"
          name={calcType}
          id={calcType}
          value={dimensions[calcType]}
          onChange={handleInputChange}
          placeholder={`Enter the ${calcTypeOption.name}`}
          className="form-control input-md focus:ring-2"
        />
      </div>
    );
  };

  const renderButtons = () => {
    const bagCount = numMulchBags > 0 ? `(${numMulchBags})` : '';
    const isVisible = isBlogMode ? calcType && dimensions[calcType] : mulchAmount > 0;

    return (
      <div className={isVisible ? 'button-container' : 'hidden'}>
        <button className='button button--primary' onClick={addToCart}>
          Add to Cart {bagCount}
        </button>
        {isBlogMode ? (
          <a className='button button--primary' href={mulchConfig.ground_smart_product_url} target="_blank">
            Add to Quote {bagCount}
          </a>
        ) : (
          <AddToQuote quoteQuantity={numMulchBags} buttonWidth={'50%'} />
        )}
      </div>
    );
  };

  return (
    <>
      <div id="mulch-calc-form" className={`form-wrapper calculator-wrapper ${isBlogMode ? 'calculator-wrapper-blog' : ''}`}>
        {isBlogMode && <h2 className="heading h2">Rubber Mulch Calculator</h2>}
        <p><strong>Accurate measurements of your area are essential to ensure you have enough mulch.</strong></p>

        <div className="form__input-row">
          <div className="form__input-wrapper params-wrapper">
            <label className="control-label toggle-calc-label" htmlFor="toggle_calc_type">Select Measurement</label>
            <div className="select-wrapper select-wrapper--primary toggle-calc-wrapper" >
              <svg focusable="false" className="icon icon--arrow-bottom" viewBox="0 0 12 8" role="presentation">
                <path stroke="currentColor" strokeWidth="2" d="M10 2L6 6 2 2" fill="none" strokeLinecap="round"></path>
              </svg>
              <select name="toggle_calc_type" id="toggle_calc_type" onChange={toggleCalcType} className="form-control">
                <option value="" disabled>Select Measurement...</option>
                {calcTypeOptions.map((option, index) => (
                  <option key={index} value={option.value}>{option.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className={calcType == 'cubic_feet' || calcType == 'cubic_yards' ? 'form__input-wrapper params-wrapper hidden' : 'form__input-wrapper params-wrapper'} >
            <label className="control-label depth-label" htmlFor="depth">Select Depth</label>
            <div className="select-wrapper select-wrapper--primary depth-wrapper" >
              <svg focusable="false" className="icon icon--arrow-bottom" viewBox="0 0 12 8" role="presentation">
                <path stroke="currentColor" strokeWidth="2" d="M10 2L6 6 2 2" fill="none" strokeLinecap="round"></path>
              </svg>
              <select name="depth" id="depth" onChange={toggleDepth} className="form-control" value={depth}>
                {depthOptions.map((option, index) => (
                  <option key={index} value={option.value}>{option.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {renderInputFields()}

        <div className="answer-container">
          <div className="text-center text-base font-semibold text-white">
            {mulchAmount > 0 && (
              <>
                <div>Required Mulch: <strong className="heading h5">{mulchAmount} lbs.</strong></div>
                <div>Bags of Mulch: <strong className="heading h5">{numMulchBags}</strong></div>
              </>
            )}
          </div>

          {renderButtons()}
        </div>
      </div>
    </>
  );
};
